import React, { useEffect, useState } from 'react';
import { useSettings } from '../../hooks/useSettings';
import { TrendingDown, TrendingUp, User, Briefcase, CreditCard, RefreshCw, Calendar } from 'lucide-react';

const apiBase = process.env.REACT_APP_BACKEND_URL;

const fetchJSON = async (url, opts = {}) => {
  const adminToken = localStorage.getItem('admin_token');
  const res = await fetch(`${apiBase}${url}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${adminToken}`,
      ...(opts.headers || {})
    }
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

const PartnerWalletLedgerTab = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { safeSettings } = useSettings();
  const displayTz = safeSettings?.timezone || 'UTC';

  // Format date with timezone (treating DB time as UTC)
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
      const d = new Date(utcDateString);
      
      return d.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: displayTz
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  const loadLedger = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ 
        page: String(page), 
        page_size: '25' 
      });
      
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      
      const data = await fetchJSON(`/api/admin/partner/ledger?${params.toString()}`);
      setTransactions(data.items || []);
      setTotal(data.total || 0);
      setHasNext(data.has_next || false);
      setHasPrev(data.has_prev || false);
    } catch (e) { 
      console.error('Failed to load ledger:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    loadLedger(); 
  }, [page, dateFrom, dateTo]);

  const handleDateFilterChange = () => {
    setPage(1); // Reset to first page when filters change
    loadLedger();
  };

  const clearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const getTransactionIcon = (transaction) => {
    if (transaction.amount > 0) {
      return <TrendingUp size={16} className="text-green-600" />;
    } else {
      return <TrendingDown size={16} className="text-red-600" />;
    }
  };

  const getTransactionDescription = (transaction) => {
    const { source, type, user_info, job_info, topup_info, metadata } = transaction;
    
    if (source === 'topup') {
      if (topup_info) {
        return `Wallet Top-up (${topup_info.currency} ${topup_info.amount} via ${topup_info.pg})`;
      }
      return 'Wallet Top-up';
    } else if (source === 'job_execution') {
      if (user_info && job_info) {
        return `Job Execution: "${job_info.list_name}" by ${user_info.full_name || user_info.email}`;
      } else if (user_info) {
        return `Job Execution by ${user_info.full_name || user_info.email}`;
      }
      return 'Job Execution';
    } else if (source === 'job_refund') {
      if (user_info && job_info) {
        return `Job Refund: "${job_info.list_name}" for ${user_info.full_name || user_info.email}`;
      } else if (user_info) {
        return `Job Refund for ${user_info.full_name || user_info.email}`;
      }
      return 'Job Refund';
    } else if (source === 'refund') {
      if (topup_info) {
        return `Topup Refund (${topup_info.currency} ${topup_info.amount})`;
      }
      return 'Topup Refund';
    } else if (source === 'manual') {
      return 'Manual Adjustment';
    }
    
    return transaction.reason || 'Unknown Transaction';
  };

  const getTransactionDetails = (transaction) => {
    const { user_info, job_info, topup_info, metadata } = transaction;
    
    const details = [];
    
    if (user_info) {
      details.push({
        label: 'User',
        value: user_info.full_name || user_info.email,
        icon: <User size={14} className="text-gray-500" />
      });
      
      if (user_info.company_name) {
        details.push({
          label: 'Company',
          value: user_info.company_name,
          icon: <Briefcase size={14} className="text-gray-500" />
        });
      }
    }
    
    if (job_info) {
      details.push({
        label: 'Contacts',
        value: job_info.number_of_contacts?.toLocaleString(),
        icon: <CreditCard size={14} className="text-gray-500" />
      });
    }
    
    if (topup_info && topup_info.pg) {
      details.push({
        label: 'Payment Method',
        value: topup_info.pg.toUpperCase(),
        icon: <CreditCard size={14} className="text-gray-500" />
      });
    }
    
    return details;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Partner Wallet Ledger</h2>
          <p className="text-gray-600 mt-1">Complete transaction history with user and job details</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            Timezone: <span className="font-medium">{displayTz}</span>
          </div>
          <button 
            onClick={loadLedger}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-200 hover:bg-blue-100 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Date Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter by Date:</span>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">From:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">To:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {(dateFrom || dateTo) && (
            <button
              onClick={clearDateFilter}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <RefreshCw size={32} className="animate-spin text-gray-400" />
          <span className="ml-3 text-gray-600">Loading transactions...</span>
        </div>
      )}

      {/* Transactions List */}
      {!loading && (
        <div className="bg-white rounded-lg shadow">
          {transactions.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">No transactions yet</h3>
              <p className="text-gray-500">Partner wallet transactions will appear here</p>
            </div>
          ) : (
            <>
              {/* Transaction Items */}
              <div className="divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <div key={transaction.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      {/* Left Side - Transaction Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {getTransactionIcon(transaction)}
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {getTransactionDescription(transaction)}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {formatDate(transaction.timestamp)} • Reference: {transaction.reference_id?.substring(0, 8)}...
                            </p>
                          </div>
                        </div>
                        
                        {/* Transaction Details */}
                        {getTransactionDetails(transaction).length > 0 && (
                          <div className="flex flex-wrap gap-4 mt-3">
                            {getTransactionDetails(transaction).map((detail, idx) => (
                              <div key={idx} className="flex items-center gap-1 text-sm text-gray-600">
                                {detail.icon}
                                <span className="font-medium">{detail.label}:</span>
                                <span>{detail.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Right Side - Amount and Balance */}
                      <div className="text-right ml-6">
                        <div className={`text-lg font-bold ${
                          transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()} credits
                        </div>
                        <div className="text-sm text-gray-500">
                          Balance: {transaction.balance_after.toLocaleString()} credits
                        </div>
                        <div className={`text-xs px-2 py-1 rounded-full mt-1 inline-block ${
                          transaction.type === 'CREDIT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {transaction.type}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {Math.min((page - 1) * 25 + 1, total)} - {Math.min(page * 25, total)} of {total.toLocaleString()} transactions
                  {(dateFrom || dateTo) && (
                    <span className="ml-2 text-blue-600">
                      (filtered {dateFrom && `from ${dateFrom}`} {dateFrom && dateTo && '- '} {dateTo && `to ${dateTo}`})
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => p - 1)}
                    disabled={!hasPrev}
                    className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm font-medium">
                    Page {page}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={!hasNext}
                    className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PartnerWalletLedgerTab;
